/**
 * Intelligent CSV Parser
 * Automatically detects data orientation, headers, metrics, and time periods
 */

export interface ParsedData {
  orientation: 'metrics-in-rows' | 'metrics-in-columns';
  metrics: ParsedMetric[];
  timePeriods: string[];
  rawData: any[][];
  confidence: number;
  summary: {
    totalMetrics: number;
    totalDataPoints: number;
    detectedFormat: string;
  };
}

export interface ParsedMetric {
  metricName: string;
  values: { [period: string]: number };
  unit?: string;
  metricType?: string;
}

class IntelligentCSVParser {
  /**
   * Main parsing function - intelligently detects format and parses
   */
  async parse(csvText: string): Promise<ParsedData> {
    // Step 1: Parse CSV into raw 2D array
    const rawData = this.parseCSVToArray(csvText);

    console.log('📊 CSV Parse Step 1 - Raw Data:', {
      totalRows: rawData.length,
      headers: rawData[0],
      firstDataRow: rawData[1],
      sampleRows: rawData.slice(0, 3)
    });

    if (rawData.length < 2) {
      throw new Error('CSV must have at least 2 rows (header + data)');
    }

    // Step 2: Detect orientation
    const orientation = this.detectOrientation(rawData);

    console.log('📊 CSV Parse Step 2 - Orientation:', orientation);

    // Step 3: Parse based on detected orientation
    let result;
    if (orientation === 'metrics-in-rows') {
      result = this.parseMetricsInRows(rawData);
    } else {
      result = this.parseMetricsInColumns(rawData);
    }

    console.log('📊 CSV Parse Step 3 - Parsed Result:', {
      orientation: result.orientation,
      metricsCount: result.metrics.length,
      timePeriodsCount: result.timePeriods.length,
      metrics: result.metrics.map(m => ({ name: m.metricName, valuesCount: Object.keys(m.values).length })),
      timePeriods: result.timePeriods,
      totalDataPoints: result.summary.totalDataPoints
    });

    return result;
  }

  /**
   * Parse CSV text to 2D array
   */
  private parseCSVToArray(csvText: string): string[][] {
    const lines = csvText.split('\n').filter(line => line.trim());
    return lines.map(line => {
      // Handle quoted values with commas
      const values: string[] = [];
      let currentValue = '';
      let insideQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());

      return values.map(v => v.replace(/^"|"$/g, '')); // Remove surrounding quotes
    });
  }

  /**
   * Detect if metrics are in rows or columns
   */
  private detectOrientation(data: string[][]): 'metrics-in-rows' | 'metrics-in-columns' {
    const firstRow = data[0];
    const firstCol = data.map(row => row[0]);

    // Count numeric vs text in first row (excluding first cell)
    const firstRowNumeric = firstRow.slice(1).filter(cell => this.isNumeric(cell)).length;
    const firstRowText = firstRow.slice(1).filter(cell => !this.isNumeric(cell) && cell.length > 0).length;

    // Count numeric vs text in first column (excluding first cell)
    const firstColNumeric = firstCol.slice(1).filter(cell => this.isNumeric(cell)).length;
    const firstColText = firstCol.slice(1).filter(cell => !this.isNumeric(cell) && cell.length > 0).length;

    // Heuristics:
    // If first row has mostly text (like month names), and first col has mostly text (like metric names)
    // then metrics are in rows
    // If first row has mostly text (like metric names) and rest is numeric, metrics are in columns

    const rowScore = firstRowText + firstColNumeric; // Text headers in row, numbers in column
    const colScore = firstRowNumeric + firstColText; // Numbers in row, text in column

    return rowScore > colScore ? 'metrics-in-rows' : 'metrics-in-columns';
  }

  /**
   * Parse when metrics are in rows
   * Format:
   *           Jan-2024  Feb-2024  Mar-2024
   * Revenue   100000    120000    150000
   * Customers 1500      1600      1700
   */
  private parseMetricsInRows(data: string[][]): ParsedData {
    const headers = data[0].slice(1); // First row, skip first cell
    const timePeriods = headers.filter(h => h.length > 0);

    const metrics: ParsedMetric[] = [];

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const metricName = row[0];

      if (!metricName || metricName.length === 0) continue;

      const values: { [period: string]: number } = {};

      for (let j = 0; j < timePeriods.length; j++) {
        const value = row[j + 1];
        if (value && this.isNumeric(value)) {
          values[timePeriods[j]] = parseFloat(value.replace(/[$,\s]/g, ''));
        }
      }

      if (Object.keys(values).length > 0) {
        metrics.push({
          metricName,
          values,
          unit: this.inferUnit(metricName),
          metricType: this.inferMetricType(metricName)
        });
      }
    }

    return {
      orientation: 'metrics-in-rows',
      metrics,
      timePeriods,
      rawData: data,
      confidence: 0.85,
      summary: {
        totalMetrics: metrics.length,
        totalDataPoints: metrics.reduce((sum, m) => sum + Object.keys(m.values).length, 0),
        detectedFormat: 'Metrics in rows, time periods in columns'
      }
    };
  }

  /**
   * Parse when metrics are in columns
   * Format:
   * Month     Revenue  Customers  Transactions
   * Jan-2024  100000   1500       25000
   * Feb-2024  120000   1600       28000
   */
  private parseMetricsInColumns(data: string[][]): ParsedData {
    const headers = data[0].slice(1); // Metric names
    const metricNames = headers.filter(h => h.length > 0);

    const metrics: ParsedMetric[] = [];
    const timePeriods: string[] = [];

    // Initialize metrics
    metricNames.forEach(name => {
      metrics.push({
        metricName: name,
        values: {},
        unit: this.inferUnit(name),
        metricType: this.inferMetricType(name)
      });
    });

    // Parse data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const period = row[0];

      if (!period || period.length === 0) continue;

      timePeriods.push(period);

      for (let j = 0; j < metricNames.length; j++) {
        const value = row[j + 1];
        if (value && this.isNumeric(value)) {
          metrics[j].values[period] = parseFloat(value.replace(/[$,\s]/g, ''));
        }
      }
    }

    return {
      orientation: 'metrics-in-columns',
      metrics,
      timePeriods,
      rawData: data,
      confidence: 0.85,
      summary: {
        totalMetrics: metrics.length,
        totalDataPoints: metrics.reduce((sum, m) => sum + Object.keys(m.values).length, 0),
        detectedFormat: 'Time periods in rows, metrics in columns'
      }
    };
  }

  /**
   * Check if a value is numeric
   */
  private isNumeric(value: string): boolean {
    if (!value || value.length === 0) return false;
    const cleaned = value.replace(/[$,\s]/g, '');
    return !isNaN(parseFloat(cleaned)) && isFinite(parseFloat(cleaned));
  }

  /**
   * Infer unit from metric name
   */
  private inferUnit(metricName: string): string {
    const lower = metricName.toLowerCase();

    if (lower.includes('revenue') || lower.includes('cost') || lower.includes('price') ||
        lower.includes('arr') || lower.includes('mrr')) {
      return 'USD';
    }
    if (lower.includes('customer') || lower.includes('user') || lower.includes('count')) {
      return 'count';
    }
    if (lower.includes('transaction') || lower.includes('request') || lower.includes('usage')) {
      return 'count';
    }
    if (lower.includes('percent') || lower.includes('rate') || lower.includes('%')) {
      return '%';
    }

    return 'number';
  }

  /**
   * Infer metric type from name
   */
  private inferMetricType(metricName: string): string {
    const lower = metricName.toLowerCase();

    if (lower.includes('revenue')) return 'revenue';
    if (lower.includes('customer') || lower.includes('user')) return 'customer_count';
    if (lower.includes('transaction')) return 'transactions';
    if (lower.includes('arr')) return 'arr';
    if (lower.includes('mrr')) return 'mrr';
    if (lower.includes('usage')) return 'usage';

    return 'custom';
  }

  /**
   * Convert parsed data to business metrics format
   */
  convertToBusinessMetrics(parsedData: ParsedData, selectedMonth?: string, csvFileName?: string): any[] {
    const metrics: any[] = [];

    // Use current month if provided, otherwise extract from data
    const currentMonth = selectedMonth || this.getCurrentMonth();

    // Generate unique CSV source identifier with timestamp
    const csvSource = csvFileName ? `${csvFileName}` : `import_${Date.now()}`;
    const importTimestamp = new Date().toISOString();

    // Import raw data without manipulation
    // For metrics-in-columns: each row+column becomes a metric
    // For metrics-in-rows: each row becomes a metric with time period values

    if (parsedData.orientation === 'metrics-in-columns') {
      // Format: Product, Revenue Feb 26, Customers Feb 26
      //         BlueStacks, $91,953, 15000
      //         Product2, $29,000, 5000
      //
      // Strategy: Include row identifier in metric name for clarity
      // Result: "BlueStacks - Revenue Feb 26" = 91953

      parsedData.metrics.forEach(metric => {
        const columnHeader = metric.metricName; // e.g., "Revenue Feb 26"

        parsedData.timePeriods.forEach(rowLabel => {
          const value = metric.values[rowLabel];
          if (value !== undefined) {
            // Include row identifier in the metric name for better clarity
            // Example: "BlueStacks - Revenue Feb 26" instead of just "Revenue Feb 26"
            const metricNameWithRow = rowLabel ? `${rowLabel} - ${columnHeader}` : columnHeader;

            metrics.push({
              metricName: metricNameWithRow,  // Include row identifier
              metricType: metric.metricType || 'custom',
              value: value,
              unit: metric.unit || 'number',
              month: currentMonth,  // Store in current viewing month
              dateRange: {
                start: `${currentMonth}-01`,
                end: this.getMonthEndDate(currentMonth)
              },
              source: 'csv_import',
              metadata: {
                notes: `${rowLabel} - ${columnHeader} from ${csvFileName}`,
                csvSource: csvSource,          // Track which CSV file
                csvFileName: csvFileName,       // Original filename
                importTimestamp: importTimestamp,
                csvRow: rowLabel,              // e.g., "BlueStacks"
                csvColumn: columnHeader,       // e.g., "Revenue Feb 26"
                originalHeader: columnHeader,  // Preserve original header (for aggregations)
                rowIdentifier: rowLabel,       // The row label/product name
                orientation: parsedData.orientation
              }
            });
          }
        });
      });
    } else {
      // metrics-in-rows: Use current month for all metrics
      parsedData.metrics.forEach(metric => {
        parsedData.timePeriods.forEach(period => {
          const value = metric.values[period];
          if (value !== undefined) {
            metrics.push({
              metricName: metric.metricName,
              metricType: metric.metricType || 'custom',
              value: value,
              unit: metric.unit || 'number',
              month: currentMonth,  // Store in current viewing month
              dateRange: {
                start: `${currentMonth}-01`,
                end: this.getMonthEndDate(currentMonth)
              },
              source: 'csv_import',
              metadata: {
                notes: `Imported from ${csvSource}`,
                csvSource: csvSource,           // Track which CSV file
                csvFileName: csvFileName,        // Original filename
                importTimestamp: importTimestamp,
                csvColumn: period,
                csvRow: metric.metricName,
                originalPeriod: period,         // Preserve original period label
                orientation: parsedData.orientation
              }
            });
          }
        });
      });
    }

    console.log('🔍 Raw CSV Import:', {
      csvSource,
      csvFileName,
      orientation: parsedData.orientation,
      targetMonth: currentMonth,
      totalMetrics: metrics.length,
      importTimestamp,
      sampleData: metrics.slice(0, 5).map(m => ({
        name: m.metricName,
        value: m.value,
        unit: m.unit,
        month: m.month,
        row: m.metadata.csvRow,
        column: m.metadata.csvColumn
      })),
      uniqueMetricNames: Array.from(new Set(metrics.map(m => m.metricName))).slice(0, 10)
    });

    return metrics;
  }

  /**
   * Get current month in YYYY-MM format
   */
  private getCurrentMonth(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Get last day of month
   */
  private getMonthEndDate(monthStr: string): string {
    const [year, month] = monthStr.split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return `${monthStr}-${String(lastDay).padStart(2, '0')}`;
  }

  /**
   * Extract YYYY-MM format from various period formats
   */
  private extractMonthFromPeriod(period: string): string {
    // Try to parse common formats: Jan-2024, 2024-01, January 2024, Revenue Feb 26, etc.
    const monthMap: { [key: string]: string } = {
      'jan': '01', 'january': '01',
      'feb': '02', 'february': '02',
      'mar': '03', 'march': '03',
      'apr': '04', 'april': '04',
      'may': '05',
      'jun': '06', 'june': '06',
      'jul': '07', 'july': '07',
      'aug': '08', 'august': '08',
      'sep': '09', 'september': '09',
      'oct': '10', 'october': '10',
      'nov': '11', 'november': '11',
      'dec': '12', 'december': '12'
    };

    const lower = period.toLowerCase().trim();

    // Try YYYY-MM format
    if (/^\d{4}-\d{2}$/.test(period)) {
      return period;
    }

    // Try to find month name and year anywhere in the string (e.g., "Revenue Feb 26")
    // Match patterns like "Feb 26", "February 2026", "Jan-2024", etc.
    for (const [monthName, monthNum] of Object.entries(monthMap)) {
      // Look for month name followed by optional separator and 2 or 4 digit year
      const pattern1 = new RegExp(`${monthName}[-\\s]?(\\d{2,4})`, 'i');
      const match = lower.match(pattern1);
      if (match) {
        let year = match[1];
        // Convert 2-digit year to 4-digit (26 -> 2026)
        if (year.length === 2) {
          year = '20' + year;
        }
        return `${year}-${monthNum}`;
      }
    }

    // Try Month-YYYY format (Jan-2024) as fallback
    const match1 = lower.match(/^([a-z]+)[-\s]?(\d{4})$/);
    if (match1) {
      const month = monthMap[match1[1]];
      if (month) return `${match1[2]}-${month}`;
    }

    // Try YYYY-Month format
    const match2 = lower.match(/^(\d{4})[-\s]?([a-z]+)$/);
    if (match2) {
      const month = monthMap[match2[2]];
      if (month) return `${match2[1]}-${month}`;
    }

    // Default to current month
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  private getMonthStart(period: string): string {
    const month = this.extractMonthFromPeriod(period);
    return `${month}-01`;
  }

  private getMonthEnd(period: string): string {
    const month = this.extractMonthFromPeriod(period);
    const [year, mon] = month.split('-').map(Number);
    const lastDay = new Date(year, mon, 0).getDate();
    return `${month}-${String(lastDay).padStart(2, '0')}`;
  }
}

export default new IntelligentCSVParser();
